/**
 * MTCommonTable - Metadata-driven configurable table for multi-territory.
 * Matches India CommonTable visual style but is a separate component to avoid cross-impact.
 *
 * Key differences from India CommonTable:
 * - Columns configured via metadata API response
 * - Edit mode makes selected row's editable cells inline-editable
 * - Supports optional Action column with edit/delete icons
 *
 * Column width / wrap behavior (applies to header + body data cells):
 * - tableLayout: 'auto' → columns size to their content.
 * - minWidth 100px floor, maxWidth 200px cap (or col.width if provided).
 * - Short values (e.g. "Yes"/"No") stay narrow; values wider than 200px WRAP
 *   onto the next line (whiteSpace: normal + overflowWrap: break-word) instead
 *   of truncating. No ellipsis, no per-cell tooltip on data cells.
 */
import DeleteIcon from '@/assets/icons/DeleteIcon'
import EditIcon from '@/assets/icons/EditIcon'
import type {
  MTMetadataField,
  MTMetadataTableColumn,
} from '@/multi-territory/mt-types/mt-metadata'
import { formatMTHeaderLabel } from '@/multi-territory/mt-utils/mt-search-fields'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

dayjs.extend(customParseFormat)

// Header labels are formatted with the SHARED MT formatter
// (`formatMTHeaderLabel`) so the table header and the advance-search field
// label for the same column always render identically (sentence case /
// "running text", acronyms such as ID/TIN kept uppercase). Keep a local alias
// so the existing call sites below read unchanged.
const formatHeaderLabel = formatMTHeaderLabel

/**
 * Estimate a STABLE column width (px) from the header label so columns keep the
 * SAME width before and after data loads. Without this, the loading skeleton
 * (empty cells) lets `tableLayout: 'auto'` spread columns evenly to fill 100%,
 * then real data makes them shrink-wrap — so columns visibly jump from wide to
 * squeezed. A deterministic per-column width (applied via <colgroup>) removes
 * that jump: the skeleton and the loaded grid render identical widths.
 *
 * Heuristic: ~8px per character of the longest word in the label (so the header
 * can wrap to ~2 lines without clipping), clamped to the 140–200px band the
 * cells already use. The Action column keeps its own fixed width.
 */
const HEADER_CHAR_PX = 8
const COL_MIN_PX = 140
const COL_MAX_PX = 200

// Smallest width a column may be dragged down to. Keeps the resize handle and a
// sliver of content reachable so a column can't be collapsed to nothing.
const COL_RESIZE_MIN_PX = 60

function estimateColumnWidth(
  col: MTMetadataTableColumn,
  overrideWidthPx?: number
): string | undefined {
  // A user-dragged width wins over everything (even col.width / Action rules)
  // so manual resizing always sticks.
  if (overrideWidthPx != null) return `${overrideWidthPx}px`
  // Action column is sized separately (icons) — let its cell rules apply.
  if (col.id === '_action') return col.minWidth || '96px'
  // Respect an explicit width first.
  if (col.width) return col.width
  const label = col.label || ''
  // Longest single word drives the min sensible width (a word won't break),
  // then clamp into the shared 140–200 band.
  const longestWord = label
    .trim()
    .split(/\s+/)
    .reduce((max, w) => Math.max(max, w.length), 0)
  const estimated = longestWord * HEADER_CHAR_PX + 32 // + cell padding
  const clamped = Math.min(COL_MAX_PX, Math.max(COL_MIN_PX, estimated))
  return `${clamped}px`
}

// Shared menu props so the dropdown popover matches the cell width instead of
// spilling across the page.
const EDIT_MENU_PROPS = {
  PaperProps: {
    sx: {
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      border: '1px solid var(--border-strong, #8E95A2)',
      borderRadius: '4px',
      mt: 0.5,
      maxHeight: '300px',
      // Match the anchor (cell) width, capped so long labels wrap instead of
      // stretching the menu across the whole page.
      maxWidth: '320px',
    },
  },
  MenuListProps: {
    sx: {
      '& .MuiMenuItem-root': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
  },
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
}

// --- Lazy Edit Cell (performance optimization) ---
// Instead of mounting heavy MUI inputs for ALL cells in bulk edit mode,
// render a lightweight placeholder that only mounts the real input on click/focus.

type LazyEditCellProps = {
  value: string
  col: MTMetadataTableColumn
  rowId: string
  metaField?: MTMetadataField
  onFieldChange?: (rowId: string, fieldKey: string, value: string) => void
  /** When true, the cell is highlighted with a red border (validation error). */
  hasError?: boolean
}

// Red border shown on cells that failed validation on Save.
const ERROR_BORDER_COLOR = 'var(--forbrand-error, #E0301E)'

// Brand-colored hover/focus border for edit inputs (orange), matching the
// MT dropdowns/search fields — replaces MUI's default blue focus outline.
const EDIT_INPUT_BORDER_SX = {
  // Target the OutlinedInput root (the element that receives Mui-focused),
  // not the TextField root — otherwise the focus rule never matches and MUI's
  // default blue focus outline shows through.
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--border-strong, #8E95A2)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--forbrand-secondary, #626771)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--forbrand-color, #fd5108)',
    borderWidth: '1px',
  },
} as const

// Stable empty reference for rows with no edits/errors — reusing the same object
// keeps memoized rows from re-rendering just because a new {} literal was created.
const EMPTY_ROW_STATE: Record<string, any> = Object.freeze({})

// Cached plain-CSS styles for the (very numerous) edit-cell placeholders. Reused
// across all cells so React/Emotion doesn't recompute styles per instance.
const EDIT_PLACEHOLDER_STYLE: CSSProperties = {
  cursor: 'text',
  padding: '4px 8px',
  border: '1px solid var(--border-strong, #8E95A2)',
  borderRadius: '4px',
  fontSize: '13px',
  minHeight: '28px',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
}
const EDIT_PLACEHOLDER_ERROR_STYLE: CSSProperties = {
  ...EDIT_PLACEHOLDER_STYLE,
  border: `1.5px solid ${ERROR_BORDER_COLOR}`,
  backgroundColor: 'rgba(224,48,30,0.06)',
}

// Cached body-cell styles. Body cells are rendered as plain <td> (NOT MUI
// <TableCell>/sx) — with 100 rows × 80 columns, MUI's per-cell Emotion sx
// processing made entering/leaving edit mode take several seconds. Plain <td>
// with a shared style object avoids that entirely.
// Data cells WRAP (no truncation): whiteSpace normal + overflowWrap break-word.
const BODY_CELL_BASE_STYLE: CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--border-subtel)',
  // Single-line + ellipsis: the column width is user-controlled via the drag
  // handle on the header border, so cells clamp to that width and expose the
  // full value through a native `title` tooltip instead of wrapping.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '14px',
  boxSizing: 'border-box',
  // Center content vertically so values in the same row share one center line.
  verticalAlign: 'middle',
}

// Data cell text: single line, clamped with an ellipsis. The column width is now
// user-controlled (drag the header's right border to resize), so content fits
// the width the user chose; a native `title` tooltip surfaces anything still
// truncated. Custom render() cells keep this same clamp.
const READONLY_CELL_TEXT_STYLE: CSSProperties = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

// Total (aggregate) row cell styling. Bold values on a tinted background so the
// pinned totals read as a summary row distinct from the data rows below. Pinned
// just under the sticky header via position:sticky.
const TOTAL_ROW_BG = 'var(--bg-layer-1, #f5f6f8)'
const TOTAL_CELL_BASE_STYLE: CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--border-subtel)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '14px',
  fontWeight: 700,
  color: 'var(--forbrand-primary)',
  backgroundColor: TOTAL_ROW_BG,
  boxSizing: 'border-box',
  verticalAlign: 'middle',
}

const CHECKBOX_CELL_STYLE: CSSProperties = {
  padding: '12px 8px',
  borderBottom: '1px solid var(--border-subtel)',
  borderRight: '1px solid var(--border-subtel)',
  width: '50px',
  minWidth: '50px',
  maxWidth: '50px',
  textAlign: 'center',
  // Center the checkbox vertically so it lines up with the (now center-aligned)
  // row content regardless of how many lines a wrapped cell occupies.
  verticalAlign: 'middle',
  boxSizing: 'border-box',
}

// Inject the hover border once (cheap) instead of per-cell sx '&:hover'.
if (
  typeof document !== 'undefined' &&
  !document.getElementById('mt-edit-placeholder-style')
) {
  const styleEl = document.createElement('style')
  styleEl.id = 'mt-edit-placeholder-style'
  // Brand-colored hover/focus border (orange) instead of MUI default blue.
  styleEl.textContent =
    '.mt-edit-placeholder:hover{border-color:var(--forbrand-color,#fd5108)!important;}'
  document.head.appendChild(styleEl)
}

const LazyEditCell = memo(function LazyEditCell({
  value,
  col,
  rowId,
  metaField,
  onFieldChange,
  hasError = false,
}: LazyEditCellProps) {
  const [active, setActive] = useState(false)

  const handleBlur = useCallback(() => {
    // Keep active for dropdowns (they manage their own close)
    if (col.type !== 'dropdown') {
      setActive(false)
    }
  }, [col.type])

  const handleActivate = useCallback(() => setActive(true), [])

  // When not active, show a lightweight editable-looking placeholder.
  // IMPORTANT: this is a plain <div> with a shared inline style object — NOT an
  // MUI <Box>/sx. In bulk edit there can be thousands of these; MUI's per-element
  // sx/Emotion serialization made entering/leaving edit mode take several seconds.
  // A plain div with a cached style mounts an order of magnitude faster.
  if (!active) {
    return (
      <div
        onClick={handleActivate}
        className="mt-edit-placeholder"
        style={hasError ? EDIT_PLACEHOLDER_ERROR_STYLE : EDIT_PLACEHOLDER_STYLE}
      >
        {value || (
          <span style={{ color: '#999' }}>
            {col.type === 'dropdown' ? 'Select' : '\u00A0'}
          </span>
        )}
      </div>
    )
  }

  // Active: render actual input.
  // Date columns use the MUI DatePicker (DD/MM/YYYY calendar), matching the
  // MT service-master edit experience.
  if (col.type === 'date') {
    const parsed =
      value && dayjs(value, 'DD/MM/YYYY', true).isValid()
        ? dayjs(value, 'DD/MM/YYYY')
        : null
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={parsed}
          format="DD/MM/YYYY"
          open={active}
          onClose={() => setActive(false)}
          onChange={(d: dayjs.Dayjs | null) =>
            onFieldChange?.(
              rowId,
              col.id,
              d && d.isValid() ? d.format('DD/MM/YYYY') : ''
            )
          }
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              placeholder: 'DD/MM/YYYY',
              onClick: () => setActive(true),
              sx: {
                '& .MuiInputBase-input': { fontSize: '13px', py: '4px' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasError
                    ? ERROR_BORDER_COLOR
                    : 'var(--border-strong, #8E95A2)',
                  borderWidth: hasError ? '1.5px' : undefined,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasError
                    ? ERROR_BORDER_COLOR
                    : 'var(--forbrand-secondary, #626771)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasError
                    ? ERROR_BORDER_COLOR
                    : 'var(--forbrand-color, #fd5108)',
                },
              },
            },
            popper: {
              // Keep the calendar above the table so it isn't clipped by the
              // scroll container.
              sx: { zIndex: 2000 },
            },
          }}
        />
      </LocalizationProvider>
    )
  }

  if (col.type === 'dropdown' && metaField?.options) {
    return (
      <Select
        size="small"
        value={value || ''}
        open={true}
        onClose={() => setActive(false)}
        onChange={e => {
          onFieldChange?.(rowId, col.id, e.target.value as string)
          setActive(false)
        }}
        IconComponent={KeyboardArrowDown}
        displayEmpty
        fullWidth
        MenuProps={EDIT_MENU_PROPS}
        sx={{
          fontSize: '13px',
          '& .MuiSelect-select': {
            py: '4px',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: hasError
              ? ERROR_BORDER_COLOR
              : 'var(--border-strong, #8E95A2)',
            borderWidth: hasError ? '1.5px' : undefined,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: hasError
              ? ERROR_BORDER_COLOR
              : 'var(--forbrand-secondary, #626771)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: hasError
              ? ERROR_BORDER_COLOR
              : 'var(--forbrand-color, #fd5108)',
          },
        }}
      >
        <MenuItem value="" disabled>
          <span style={{ color: '#999' }}>Select</span>
        </MenuItem>
        {metaField.options.map(opt => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{
              fontSize: '13px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    )
  }

  if (col.type === 'number') {
    return (
      <TextField
        type="text"
        inputMode="decimal"
        size="small"
        value={value}
        autoFocus
        onBlur={handleBlur}
        onChange={e => {
          const v = e.target.value
          if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v))
            onFieldChange?.(rowId, col.id, v)
        }}
        sx={{
          '& .MuiInputBase-input': { fontSize: '13px', py: '4px' },
          ...(hasError
            ? {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: ERROR_BORDER_COLOR,
                  borderWidth: '1.5px',
                },
              }
            : EDIT_INPUT_BORDER_SX),
        }}
        fullWidth
      />
    )
  }

  return (
    <TextField
      size="small"
      value={value}
      autoFocus
      onBlur={handleBlur}
      onChange={e => onFieldChange?.(rowId, col.id, e.target.value)}
      sx={{
        '& .MuiInputBase-input': { fontSize: '13px', py: '4px' },
        ...(hasError
          ? {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: ERROR_BORDER_COLOR,
                borderWidth: '1.5px',
              },
            }
          : EDIT_INPUT_BORDER_SX),
      }}
      fullWidth
    />
  )
})

// --- Types ---

export type MTCommonTableProps = {
  columns: MTMetadataTableColumn[]
  data: Record<string, any>[]
  loading?: boolean
  showCheckbox?: boolean
  showFooter?: boolean
  emptyMessage?: string
  skeletonRows?: number
  /**
   * Optional aggregated totals row pinned directly under the sticky header
   * (matches the India Prepare page's total row). Keyed by column id
   * (field_key) -> aggregated value. Columns absent from this map render blank
   * in the total row. When null/undefined the total row is not shown.
   *
   * The values are display-ready (already summed/formatted). This keeps the
   * table dumb: whoever owns the data (client aggregate now, API `summary`
   * later) decides what each column's total is.
   */
  totals?: Record<string, number | string> | null
  /** Label shown in the first non-total column of the total row. Default "Total". */
  totalsLabel?: string
  // Selection
  selectedItems?: string[]
  onSelectItem?: (uuid: string) => void
  onSelectAll?: () => void
  // Edit mode (inline)
  editMode?: boolean
  editingRowId?: string | null
  /**
   * Selection-driven edit (MTWHT): when provided, ONLY these row ids are
   * editable while editMode is on, and editingData is keyed by row id.
   * Takes precedence over editingRowId.
   */
  editableRowIds?: string[]
  editingData?: Record<string, any>
  onFieldChange?: (rowId: string, fieldKey: string, value: string) => void
  /**
   * Per-cell validation errors to highlight with a red border, keyed by
   * rowId -> fieldKey -> true. Set on Save when a cell fails validation.
   */
  fieldErrors?: Record<string, Record<string, boolean>>
  /** Raw metadata fields for dropdown options and field type detection */
  rawMetadataFields?: MTMetadataField[]
  /** Callback for edit action button click (row uuid) */
  onRowEdit?: (uuid: string) => void
  /** Callback for delete action button click (row uuid) */
  onRowDelete?: (uuid: string) => void
  /** Function to determine if edit button should be disabled for a row */
  isEditDisabled?: (row: Record<string, any>) => boolean
  /** Function to determine if delete button should be disabled for a row */
  isDeleteDisabled?: (row: Record<string, any>) => boolean
  // Pagination
  totalItems?: number
  currentPage?: number
  itemsPerPage?: number
  onPageChange?: (page: number) => void
  onItemsPerPageChange?: (size: number) => void
  itemsPerPageOptions?: number[]
  /** Show "N items selected" in the footer. Default true. */
  showSelectedCountInFooter?: boolean
}

// --- Skeleton Loading ---

function MTTableSkeleton({
  rows,
  columns,
  showCheckbox,
}: {
  rows: number
  columns: number
  showCheckbox: boolean
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={`skeleton-${rowIndex}`}>
          {showCheckbox && (
            <TableCell
              sx={{
                borderRight: '1px solid var(--border-subtel)',
                borderBottom: '1px solid var(--border-subtel)',
                width: '50px',
                padding: '12px 16px',
                textAlign: 'center',
                verticalAlign: 'middle',
              }}
            >
              <Skeleton variant="rectangular" width={15} height={15} />
            </TableCell>
          )}
          {Array.from({ length: columns }).map((_, colIndex) => (
            <TableCell
              key={colIndex}
              sx={{
                borderRight:
                  colIndex < columns - 1
                    ? '1px solid var(--border-subtel)'
                    : 'none',
                borderBottom: '1px solid var(--border-subtel)',
                padding: '12px 16px',
                verticalAlign: 'middle',
              }}
            >
              <Skeleton variant="text" width="100%" height={20} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

// --- Footer (matches India CommonTable) ---

function MTTableFooter({
  totalItems = 0,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  selectedCount = 0,
}: {
  totalItems: number
  currentPage: number
  itemsPerPage: number
  onPageChange?: (page: number) => void
  onItemsPerPageChange?: (size: number) => void
  itemsPerPageOptions?: number[]
  selectedCount?: number
}) {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        padding: '4px',
        borderTop: '1px solid var(--border-subtel)',
        backgroundColor: '#fafafa',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {selectedCount > 0 && (
          <Typography
            variant="body2"
            color="var(--forbrand-color)"
            sx={{ fontWeight: 500 }}
          >
            {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Items per page:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 70 }}>
            <Select
              value={itemsPerPage}
              onChange={e => onItemsPerPageChange?.(Number(e.target.value))}
              sx={{
                fontSize: '14px',
                '& .MuiSelect-select': { padding: '4px 8px' },
                '& .MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 0,
                },
              }}
            >
              {itemsPerPageOptions.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body2" sx={{ color: '#CF6210' }}>
          {totalItems === 0 ? '0 of 0' : `${endItem} of ${totalItems}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {totalPages > 1 && (
          <>
            <Tooltip title="First Page">
              <IconButton
                size="small"
                onClick={() => onPageChange?.(1)}
                disabled={currentPage === 1}
                sx={{ color: currentPage === 1 ? '#ccc' : '#666' }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Previous Page">
              <IconButton
                size="small"
                onClick={() => onPageChange?.(currentPage - 1)}
                disabled={currentPage === 1}
                sx={{ color: currentPage === 1 ? '#ccc' : '#666' }}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_e, page) => onPageChange?.(page)}
                size="small"
                shape="rounded"
                showFirstButton={false}
                showLastButton={false}
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '12px',
                    minWidth: '28px',
                    height: '22px',
                  },
                }}
              />
            </Box>
            <Tooltip title="Next Page">
              <IconButton
                size="small"
                onClick={() => onPageChange?.(currentPage + 1)}
                disabled={currentPage === totalPages}
                sx={{ color: currentPage === totalPages ? '#ccc' : '#666' }}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Last Page">
              <IconButton
                size="small"
                onClick={() => onPageChange?.(totalPages)}
                disabled={currentPage === totalPages}
                sx={{ color: currentPage === totalPages ? '#ccc' : '#666' }}
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  )
}

// --- Memoized Row (performance) ---
// Bulk edit can involve thousands of cells (e.g. 80 cols × 100 rows). Without
// memoization, a keystroke in ONE cell re-renders EVERY row/cell. This row is
// memoized on its own slice of edit data / errors / selection so a keystroke
// only re-renders the row being edited.

type MTTableRowProps = {
  row: Record<string, any>
  rowId: string
  columns: MTMetadataTableColumn[]
  showCheckbox: boolean
  isSelected: boolean
  onSelectItem?: (uuid: string) => void
  editMode: boolean
  isRowEditing: boolean
  /** This row's edited values only ({ fieldKey: value }). */
  rowEditData: Record<string, any>
  /** This row's error flags only ({ fieldKey: true }). */
  rowErrors: Record<string, boolean>
  onFieldChange?: (rowId: string, fieldKey: string, value: string) => void
  /** field_key -> metadata field, for dropdown options / type detection. */
  metaFieldMap: Map<string, MTMetadataField>
  onRowEdit?: (uuid: string) => void
  onRowDelete?: (uuid: string) => void
  isEditDisabled?: (row: Record<string, any>) => boolean
  isDeleteDisabled?: (row: Record<string, any>) => boolean
  /** User-dragged column widths (px), keyed by column id. */
  columnWidths: Record<string, number>
}

const MTTableRow = memo(
  function MTTableRow({
    row,
    rowId,
    columns,
    showCheckbox,
    isSelected,
    onSelectItem,
    editMode,
    isRowEditing,
    rowEditData,
    rowErrors,
    onFieldChange,
    metaFieldMap,
    onRowEdit,
    onRowDelete,
    isEditDisabled,
    isDeleteDisabled,
    columnWidths,
  }: MTTableRowProps) {
    // Viewport-based lazy editing: mounting editable inputs for ALL rows at once
    // (e.g. 100 rows × 80 cols = 8000 inputs) freezes the tab for seconds when
    // entering edit mode. Instead, a row only mounts its editable inputs once it
    // is scrolled near the viewport; off-screen rows show cheap read-only text.
    // This keeps entering/leaving edit mode instant regardless of row count.
    const rowRef = useRef<HTMLTableRowElement>(null)
    const [isRowVisible, setIsRowVisible] = useState(false)

    useEffect(() => {
      if (!isRowEditing) return
      const el = rowRef.current
      if (!el || typeof IntersectionObserver === 'undefined') {
        // No observer support → fall back to always-editable.
        setIsRowVisible(true)
        return
      }
      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setIsRowVisible(true)
              // Once visible we keep the inputs mounted; no need to keep observing.
              observer.disconnect()
              break
            }
          }
        },
        // Generous margin so rows just below the fold are ready before they
        // scroll into view (avoids a text→input flash).
        { root: null, rootMargin: '400px 0px', threshold: 0 }
      )
      observer.observe(el)
      return () => observer.disconnect()
    }, [isRowEditing])

    // Editable inputs render only when the row is editing AND on/near screen.
    const renderEditable = isRowEditing && isRowVisible

    const renderCell = (col: MTMetadataTableColumn) => {
      const value =
        isRowEditing && rowEditData[col.id] !== undefined
          ? rowEditData[col.id]
          : (row[col.id] ?? '')

      // Action column - render edit/delete icons
      if (col.id === '_action') {
        const editDisabled = isEditDisabled ? isEditDisabled(row) : false
        const deleteDisabled = isDeleteDisabled ? isDeleteDisabled(row) : false
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start' }}>
            {onRowEdit && (
              // Wrap in a <span> so the tooltip still shows when the button is
              // disabled (disabled buttons don't emit hover events).
              <Tooltip title="Edit" arrow placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRowEdit(rowId)}
                    disabled={editDisabled}
                    aria-label="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onRowDelete && (
              <Tooltip title="Delete" arrow placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRowDelete(rowId)}
                    disabled={deleteDisabled}
                    aria-label="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )
      }

      if (renderEditable && col.editable) {
        // LazyEditCell renders a light placeholder until clicked (no heavy MUI
        // input mounted), so thousands of editable cells stay responsive.
        const metaField = metaFieldMap.get(col.id)
        const hasError = Boolean(rowErrors[col.id])
        return (
          <LazyEditCell
            value={value}
            col={col}
            rowId={rowId}
            metaField={metaField}
            onFieldChange={onFieldChange}
            hasError={hasError}
          />
        )
      }

      if ((col as any).render) {
        const cellValue =
          value !== ''
            ? value
            : (row[col.id] ?? row[col.id.toLowerCase()] ?? '')
        return (
          <div style={READONLY_CELL_TEXT_STYLE}>
            {(col as any).render(cellValue, row)}
          </div>
        )
      }

      if (value === null || value === undefined || value === '') return null
      const displayValue = String(value)

      // Clamp to the (user-resizable) column width with an ellipsis and attach a
      // hover tooltip so the full value is still reachable. `title` is used
      // (native tooltip) instead of MUI <Tooltip> to avoid mounting thousands of
      // MUI Tooltip instances in large grids (perf).
      return (
        <span style={READONLY_CELL_TEXT_STYLE} title={displayValue}>
          {displayValue}
        </span>
      )
    }

    return (
      <TableRow key={rowId} ref={rowRef}>
        {showCheckbox && (
          <td style={CHECKBOX_CELL_STYLE}>
            <Checkbox
              checked={isSelected}
              onChange={() => onSelectItem?.(rowId)}
              size="small"
              disabled={!row?.fileName && row?.fileSource === 'Reload'}
              sx={{
                '&.Mui-checked': { color: 'var(--forbrand-color)' },
                color: 'var(--forbrand-color)',
                padding: 0,
              }}
            />
          </td>
        )}
        {columns.map((col, colIndex) => {
          // Build the cell style from cached bases (no MUI sx / Emotion).
          // Width rules: auto-size to content, min 140px floor, max 200px cap
          // (or col.width when provided). Wrap beyond the cap — no truncation.
          // The Action column (icons) is excluded from the 140/200 rules and
          // uses its own fixed width so its content never wraps.
          const isActionCol = col.id === '_action'
          const overrideWidth = columnWidths[col.id]
          const cellStyle: CSSProperties = {
            ...BODY_CELL_BASE_STYLE,
            borderRight:
              colIndex < columns.length - 1
                ? '1px solid var(--border-subtel)'
                : 'none',
            // A user-dragged width pins the cell to that exact size; otherwise
            // the normal 140/200 band (or col.width / Action width) applies.
            ...(overrideWidth != null
              ? {
                  width: `${overrideWidth}px`,
                  minWidth: `${overrideWidth}px`,
                  maxWidth: `${overrideWidth}px`,
                }
              : {
                  ...(col.width ? { width: col.width } : null),
                  minWidth: isActionCol
                    ? col.width || '96px'
                    : col.minWidth || '140px',
                  ...(isActionCol ? {} : { maxWidth: col.width || '200px' }),
                }),
            ...(editMode && !isRowEditing
              ? { backgroundColor: '#f9f9f9', color: '#bdbdbd', opacity: 0.7 }
              : null),
            ...(isRowEditing && !col.editable
              ? { backgroundColor: '#f2f2f2', color: '#9e9e9e' }
              : null),
          }
          return (
            <td key={col.id} style={cellStyle}>
              {renderCell(col)}
            </td>
          )
        })}
      </TableRow>
    )
  },
  (prev, next) => {
    // Re-render only when something that affects THIS row changes.
    if (
      prev.row !== next.row ||
      prev.columns !== next.columns ||
      prev.isSelected !== next.isSelected ||
      prev.editMode !== next.editMode ||
      prev.isRowEditing !== next.isRowEditing ||
      prev.rowEditData !== next.rowEditData ||
      prev.rowErrors !== next.rowErrors ||
      prev.metaFieldMap !== next.metaFieldMap ||
      prev.onFieldChange !== next.onFieldChange ||
      prev.onSelectItem !== next.onSelectItem ||
      prev.columnWidths !== next.columnWidths
    ) {
      return false
    }
    return true
  }
)

// --- Total (aggregate) Row ---
// Pinned summary row rendered directly under the sticky header (matches the
// India Prepare page). Values come from the `totals` map keyed by column id;
// columns not present render blank. The label (e.g. "Total") is placed in the
// first column that has no total of its own so it reads like a row heading.

type MTTotalRowProps = {
  columns: MTMetadataTableColumn[]
  showCheckbox: boolean
  totals: Record<string, number | string>
  totalsLabel: string
  columnWidths: Record<string, number>
  /** Vertical offset (px) so the total row sticks just below the header. */
  stickyTop: number
}

const MTTotalRow = memo(function MTTotalRow({
  columns,
  showCheckbox,
  totals,
  totalsLabel,
  columnWidths,
  stickyTop,
}: MTTotalRowProps) {
  // First column (left→right) that carries no total of its own hosts the
  // "Total" label; everything before it stays blank.
  const labelColId = columns.find(c => totals[c.id] == null)?.id

  return (
    <TableRow>
      {showCheckbox && (
        <td
          style={{
            ...TOTAL_CELL_BASE_STYLE,
            borderRight: '1px solid var(--border-subtel)',
            width: '50px',
            minWidth: '50px',
            maxWidth: '50px',
            position: 'sticky',
            top: stickyTop,
            zIndex: 9,
          }}
        />
      )}
      {columns.map((col, colIndex) => {
        const isActionCol = col.id === '_action'
        const overrideWidth = columnWidths[col.id]
        const rawTotal = totals[col.id]
        const hasTotal = rawTotal != null && rawTotal !== ''
        const isLabelCell = !hasTotal && col.id === labelColId
        const display = hasTotal
          ? String(rawTotal)
          : isLabelCell
            ? totalsLabel
            : ''
        const cellStyle: CSSProperties = {
          ...TOTAL_CELL_BASE_STYLE,
          borderRight:
            colIndex < columns.length - 1
              ? '1px solid var(--border-subtel)'
              : 'none',
          position: 'sticky',
          top: stickyTop,
          zIndex: 8,
          ...(overrideWidth != null
            ? {
                width: `${overrideWidth}px`,
                minWidth: `${overrideWidth}px`,
                maxWidth: `${overrideWidth}px`,
              }
            : {
                ...(col.width ? { width: col.width } : null),
                minWidth: isActionCol
                  ? col.width || '96px'
                  : col.minWidth || '140px',
                ...(isActionCol ? {} : { maxWidth: col.width || '200px' }),
              }),
        }
        return (
          <td key={col.id} style={cellStyle} title={display || undefined}>
            <span style={READONLY_CELL_TEXT_STYLE}>{display}</span>
          </td>
        )
      })}
    </TableRow>
  )
})

// --- Main Component ---

export function MTCommonTable({
  columns,
  data,
  loading = false,
  showCheckbox = false,
  showFooter = true,
  emptyMessage = 'No data available',
  skeletonRows = 10,
  totals = null,
  totalsLabel = 'Total',
  selectedItems = [],
  onSelectItem,
  onSelectAll,
  editMode = false,
  editingRowId,
  editableRowIds,
  editingData = {},
  onFieldChange,
  fieldErrors = {},
  rawMetadataFields = [],
  onRowEdit,
  onRowDelete,
  isEditDisabled,
  isDeleteDisabled,
  totalItems = 0,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  showSelectedCountInFooter = true,
}: MTCommonTableProps) {
  // Per-column user-dragged widths (px), keyed by column id. A column the user
  // has resized by dragging its header's right border is pinned to that width
  // (overriding the default 140/200 band); untouched columns keep the default.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})

  // Live drag state. We track the column being resized, where the pointer
  // started, and that column's width at drag start, then compute the new width
  // on each pointermove. Refs avoid re-rendering on every mouse move.
  const resizeState = useRef<{
    colId: string
    startX: number
    startWidth: number
  } | null>(null)

  // Measure a column's current rendered width (header cell) so a drag that
  // starts on a never-resized column grows/shrinks from its actual on-screen
  // size rather than snapping to an estimate.
  const headerCellRefs = useRef<Record<string, HTMLTableCellElement | null>>({})

  // Measured header height so the pinned total row sticks EXACTLY below the
  // sticky header regardless of whether the (optional) group header row is
  // present or how far the labels wrap.
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  const handleResizeMove = useCallback((e: PointerEvent) => {
    const st = resizeState.current
    if (!st) return
    const delta = e.clientX - st.startX
    const next = Math.max(COL_RESIZE_MIN_PX, Math.round(st.startWidth + delta))
    setColumnWidths(prev =>
      prev[st.colId] === next ? prev : { ...prev, [st.colId]: next }
    )
  }, [])

  const handleResizeEnd = useCallback(() => {
    resizeState.current = null
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    window.removeEventListener('pointermove', handleResizeMove)
    window.removeEventListener('pointerup', handleResizeEnd)
  }, [handleResizeMove])

  const handleResizeStart = useCallback(
    (e: ReactPointerEvent, colId: string) => {
      // Don't let the drag also trigger header click/sort or text selection.
      e.preventDefault()
      e.stopPropagation()
      const measured =
        headerCellRefs.current[colId]?.getBoundingClientRect().width
      const startWidth =
        columnWidths[colId] ?? (measured ? Math.round(measured) : COL_MIN_PX)
      resizeState.current = { colId, startX: e.clientX, startWidth }
      // Visual feedback + prevent text selection while dragging.
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', handleResizeMove)
      window.addEventListener('pointerup', handleResizeEnd)
    },
    [columnWidths, handleResizeMove, handleResizeEnd]
  )

  // Safety net: detach global listeners if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handleResizeMove)
      window.removeEventListener('pointerup', handleResizeEnd)
    }
  }, [handleResizeMove, handleResizeEnd])

  const hasTotals = Boolean(totals && Object.keys(totals).length > 0)

  // Keep the measured header height in sync (header can be 1 or 2 rows and the
  // labels wrap, so the height isn't fixed). Only needed when a totals row is
  // pinned beneath it.
  useEffect(() => {
    if (!hasTotals) return
    const el = theadRef.current
    if (!el) return
    const measure = () => setHeaderHeight(el.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [hasTotals, columns, columnWidths])

  const isEmpty = !loading && data.length === 0
  const isAllSelected = data.length > 0 && selectedItems.length === data.length
  const isIndeterminate =
    selectedItems.length > 0 && selectedItems.length < data.length
  const columnsCount = columns.length + (showCheckbox ? 1 : 0)

  // Build column group spans for the two-row header
  const hasAnyGroup = columns.some(c => c.columnGroup?.key)
  const columnGroups = hasAnyGroup
    ? (() => {
        const groups: {
          key: string
          label: string
          color?: string
          colSpan: number
        }[] = []
        let i = 0
        while (i < columns.length) {
          const gKey = columns[i].columnGroup?.key
          if (!gKey) {
            groups.push({ key: `__none_${i}`, label: '', colSpan: 1 })
            i++
          } else {
            let span = 1
            while (
              i + span < columns.length &&
              columns[i + span].columnGroup?.key === gKey
            )
              span++
            groups.push({
              key: gKey,
              label: columns[i].columnGroup!.label,
              color: columns[i].columnGroup!.color,
              colSpan: span,
            })
            i += span
          }
        }
        return groups
      })()
    : []

  // field_key -> metadata field lookup (built once) so cells don't run an
  // O(fields) .find() on every render across thousands of cells.
  const metaFieldMap = useMemo(() => {
    const map = new Map<string, MTMetadataField>()
    for (const f of rawMetadataFields) map.set(f.field_key, f)
    return map
  }, [rawMetadataFields])

  const getRowId = (row: Record<string, any>, index: number) =>
    row.associated_uuid ||
    row.global_uuid ||
    row.uuid ||
    row.id ||
    String(index)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: showFooter ? 'calc(100vh - 250px)' : 'auto',
        minHeight: showFooter ? '300px' : 'auto',
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          border: '1px solid var(--border-subtel)',
          borderBottom: showFooter ? 'none' : '1px solid var(--border-subtel)',
          flex: 1,
          maxHeight: isEmpty ? 'none' : 'calc(100vh - 280px)',
          marginBottom: showFooter ? 0 : '28px',
          position: 'relative',
        }}
      >
        <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
          {/*
            tableLayout: 'fixed' + a per-column <colgroup> width → column widths
            come ONLY from the colgroup, not from cell content. This is what
            keeps the columns stable: the loading skeleton (empty cells) and the
            loaded grid (real data) render the SAME widths, so columns no longer
            jump from wide (while loading) to squeezed (after data arrives).
            Each width is derived from the header label (estimateColumnWidth),
            clamped to the 140–200px band the cells use; explicit col.width wins.
            Content still wraps within its fixed column (cells keep whiteSpace:
            normal + overflowWrap). Only the checkbox column is a fixed 50px.
          */}
          <colgroup>
            {showCheckbox && <col style={{ width: '50px' }} />}
            {columns.map(col => (
              <col
                key={col.id}
                style={{
                  width: estimateColumnWidth(col, columnWidths[col.id]),
                }}
              />
            ))}
          </colgroup>
          <TableHead
            ref={theadRef}
            sx={{ position: 'sticky', top: 0, zIndex: 10 }}
          >
            {/* Group header row — only rendered when at least one column has a column_group */}
            {hasAnyGroup && (
              <TableRow>
                {showCheckbox && (
                  <TableCell
                    rowSpan={2}
                    sx={{
                      fontWeight: 600,
                      borderBottom: '1px solid var(--border-subtel)',
                      borderRight: '1px solid var(--border-subtel)',
                      background: 'var(--table-header-bg)',
                      padding: '4px 8px',
                      width: '50px',
                      minWidth: '50px',
                      maxWidth: '50px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={() => onSelectAll?.()}
                      size="small"
                      sx={{
                        '&.Mui-checked': { color: 'var(--forbrand-color)' },
                        '&.MuiCheckbox-indeterminate': {
                          color: 'var(--forbrand-color)',
                        },
                        color: 'var(--forbrand-color)',
                        padding: 0,
                      }}
                    />
                  </TableCell>
                )}
                {columnGroups.map(g =>
                  g.label ? (
                    <TableCell
                      key={g.key}
                      colSpan={g.colSpan}
                      sx={{
                        fontWeight: 700,
                        fontSize: '13px',
                        textAlign: 'center',
                        borderBottom: '1px solid var(--border-subtel)',
                        borderRight: '1px solid var(--border-subtel)',
                        background: g.color || 'var(--table-header-bg)',
                        color: 'var(--forbrand-primary)',
                        padding: '4px 8px',
                        verticalAlign: 'middle',
                      }}
                    >
                      {formatHeaderLabel(g.label)}
                    </TableCell>
                  ) : (
                    <TableCell
                      key={g.key}
                      sx={{
                        borderBottom: '1px solid var(--border-subtel)',
                        borderRight: '1px solid var(--border-subtel)',
                        background: 'var(--table-header-bg)',
                        padding: '4px 8px',
                      }}
                    />
                  )
                )}
              </TableRow>
            )}
            {/* Column label row. No forced row height — the row grows to fit
                the (wrapped) header labels, matching the compact Figma header
                instead of clipping/cramming multi-line text into 28px. */}
            <TableRow>
              {!hasAnyGroup && showCheckbox && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    borderBottom: '1px solid var(--border-subtel)',
                    borderRight: '1px solid var(--border-subtel)',
                    background: 'var(--table-header-bg)',
                    padding: '4px 8px',
                    width: '50px',
                    minWidth: '50px',
                    maxWidth: '50px',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={() => onSelectAll?.()}
                    size="small"
                    sx={{
                      '&.Mui-checked': { color: 'var(--forbrand-color)' },
                      '&.MuiCheckbox-indeterminate': {
                        color: 'var(--forbrand-color)',
                      },
                      color: 'var(--forbrand-color)',
                      padding: 0,
                    }}
                  />
                </TableCell>
              )}
              {columns.map(col => {
                // The Action column holds icons — it must never wrap its label
                // ("Action" was breaking one letter per line when the column was
                // narrow). Keep it single-line and use its fixed width as-is.
                const isActionCol = col.id === '_action'
                const overrideWidth = columnWidths[col.id]
                return (
                  <TableCell
                    key={col.id}
                    ref={(el: HTMLTableCellElement | null) => {
                      headerCellRefs.current[col.id] = el
                    }}
                    sx={{
                      color: 'var(--forbrand-primary)',
                      borderBottom: '1px solid var(--border-subtel)',
                      borderRight: '1px solid var(--border-subtel)',
                      background:
                        col.columnGroup?.color || 'var(--table-header-bg)',
                      // Match the body cell padding exactly (12px vertical,
                      // 16px horizontal) so the header label lines up over the
                      // data both horizontally and vertically.
                      padding: '12px 16px',
                      // box-sizing must match the body <td> (border-box) so the
                      // min/max width constraints describe the SAME box on header
                      // and body — otherwise auto layout negotiates two different
                      // intrinsic widths and the columns drift out of alignment.
                      boxSizing: 'border-box',
                      // Anchor for the absolutely-positioned resize handle.
                      position: 'relative',
                      // A user-dragged width pins the header to that exact size;
                      // otherwise the normal 140/200 band (or col.width) applies.
                      ...(overrideWidth != null
                        ? {
                            width: `${overrideWidth}px`,
                            minWidth: `${overrideWidth}px`,
                            maxWidth: `${overrideWidth}px`,
                          }
                        : {
                            ...(col.width ? { width: col.width } : {}),
                            minWidth: isActionCol
                              ? col.width || '96px'
                              : col.minWidth || '140px',
                            maxWidth: isActionCol
                              ? undefined
                              : col.width || '200px',
                          }),
                      verticalAlign: 'middle',
                      textAlign: 'left',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        // Single-line clamp so the header matches the (now
                        // user-resizable) column width; full label on hover.
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: '1.3',
                        fontSize: '14px',
                        fontWeight: 600,
                      }}
                      title={formatHeaderLabel(col.label)}
                    >
                      {formatHeaderLabel(col.label)}
                    </Box>
                    {/* Drag-to-resize handle sitting on the column's right
                        border. Grab it and drag left/right to resize the column;
                        the whole column (header + body) follows via colgroup.
                        Not shown on the Action column (fixed-width icons). */}
                    {!isActionCol && (
                      <Box
                        onPointerDown={e => handleResizeStart(e, col.id)}
                        // Double-click clears the manual width (back to default).
                        onDoubleClick={() =>
                          setColumnWidths(prev => {
                            if (prev[col.id] == null) return prev
                            const next = { ...prev }
                            delete next[col.id]
                            return next
                          })
                        }
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize column"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '8px',
                          height: '100%',
                          cursor: 'col-resize',
                          userSelect: 'none',
                          touchAction: 'none',
                          zIndex: 1,
                          // Thin visible grip line, brand color on hover.
                          '&:hover::after, &:active::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: '3px',
                            width: '2px',
                            height: '100%',
                            background: 'var(--forbrand-color, #fd5108)',
                          },
                        }}
                      />
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <MTTableSkeleton
                rows={skeletonRows}
                columns={columns.length}
                showCheckbox={showCheckbox}
              />
            ) : isEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={columnsCount}
                  sx={{
                    borderBottom: 'none',
                    padding: 0,
                    background: 'transparent',
                    height: '320px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      minHeight: '320px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      zIndex: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 500,
                        mt: 2,
                      }}
                    >
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {hasTotals && (
                  <MTTotalRow
                    columns={columns}
                    showCheckbox={showCheckbox}
                    totals={totals as Record<string, number | string>}
                    totalsLabel={totalsLabel}
                    columnWidths={columnWidths}
                    stickyTop={headerHeight}
                  />
                )}
                {data.map((row, index) => {
                  const rowId = getRowId(row, index)
                  const isSelected = selectedItems.includes(rowId)
                  const isRowEditing =
                    editMode &&
                    (editableRowIds
                      ? editableRowIds.includes(rowId)
                      : editingRowId === rowId ||
                        editingRowId === null ||
                        editingRowId === undefined)
                  // Pass only THIS row's edit/error slice (stable empty ref when
                  // untouched) so the memoized row re-renders in isolation.
                  const rowEditData =
                    editableRowIds ||
                    editingRowId === null ||
                    editingRowId === undefined
                      ? editingData[rowId] || EMPTY_ROW_STATE
                      : editingData || EMPTY_ROW_STATE
                  const rowErrors = fieldErrors[rowId] || EMPTY_ROW_STATE
                  return (
                    <MTTableRow
                      key={rowId}
                      row={row}
                      rowId={rowId}
                      columns={columns}
                      showCheckbox={showCheckbox}
                      isSelected={isSelected}
                      onSelectItem={onSelectItem}
                      editMode={editMode}
                      isRowEditing={isRowEditing}
                      rowEditData={rowEditData}
                      rowErrors={rowErrors}
                      onFieldChange={onFieldChange}
                      metaFieldMap={metaFieldMap}
                      onRowEdit={onRowEdit}
                      onRowDelete={onRowDelete}
                      isEditDisabled={isEditDisabled}
                      isDeleteDisabled={isDeleteDisabled}
                      columnWidths={columnWidths}
                    />
                  )
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showFooter && totalItems > 0 && (
        <Paper
          sx={{
            border: '1px solid var(--border-subtel)',
            borderTop: 'none',
            boxShadow: 'none',
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            backgroundColor: '#FFFFFF',
            flexShrink: 0,
          }}
        >
          <MTTableFooter
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsPerPageOptions={itemsPerPageOptions}
            selectedCount={showSelectedCountInFooter ? selectedItems.length : 0}
          />
        </Paper>
      )}
    </Box>
  )
}
